import { inject, Injectable } from '@angular/core';
import { FormattedExecutionResult } from 'graphql';
import {
  ConfirmMasterfileConfigFboGQL,
  ConfirmMasterfileConfigFboMutation,
  ConfirmMasterfileConfigPackhouseConnectionsGQL,
  ConfirmMasterfileConfigPackhouseConnectionsMutation,
  CreatePackhouseConnectionsAttribute,
  CreatePackhouseConnectionsRequestGQL,
  CreatePackhouseConnectionsRequestMutation,
  FoodBusinessOperator,
  FoodBusinessOperatorsByTypeGQL,
  FoodBusinessOperatorsByTypeQuery,
  MasterfileConfig,
  MasterfileConfigFboInput,
  MasterfileConfigPackhouseConnectionAttributes,
  MasterfileConfigsGQL,
  MasterfileConfigsQuery,
  MasterfileConfigsQueryVariables,
  MasterfileConfigStateEnum,
  PackhouseConnection,
  PackhouseConnectionClientChangeRequest,
  PackhouseConnectionsClientChangeRequestsGQL,
  PackhouseConnectionsClientChangeRequestsQuery,
  PackhouseConnectionsGQL,
  PackhouseConnectionsQuery,
  AddInternalPackhouseGQL,
  AddInternalPackhouseMutation,
  AddInternalPackhouseAttributes,
  InternalPackhouseClientChangeRequest,
  InternalPackhouseClientChangeRequestsQuery,
  InternalPackhouseClientChangeRequestsGQL,
  CreateSoftwareUpdateReportGQL,
  CreateSoftwareUpdateReportMutation,
  SoftwareUpdateReportRequest,
  PackhouseSoftwareUpdateReportsQuery,
  PackhouseSoftwareUpdateReportsGQL,
} from '@app/generated/graphql';
import { map, Observable, zip } from 'rxjs';
import { IConfirmFBO } from '../interfaces/packhouse-confirmation.interface';
import { Apollo } from 'apollo-angular';

/**
 * Service responsible for fetching and transforming data for the packhouse confirmation dashboard.
 * It is used to fetch the necessary data from the API and transform it into the format required by the dashboard.
 */

@Injectable()
export class PackhouseConfirmationService {
  // dependencies
  private apollo = inject(Apollo);
  private masterfileConfigsGQL = inject(MasterfileConfigsGQL);
  private packhouseConnectionsGQL = inject(PackhouseConnectionsGQL);
  private confirmMasterfileConfigFboGQL = inject(ConfirmMasterfileConfigFboGQL);
  private confirmMasterfileConfigPackhouseConnectionsGQL = inject(
    ConfirmMasterfileConfigPackhouseConnectionsGQL,
  );
  private _foodBusinessOperatorsByTypeGQL = inject(
    FoodBusinessOperatorsByTypeGQL,
  );
  private createPackhouseConnectionsRequestGQL = inject(
    CreatePackhouseConnectionsRequestGQL,
  );
  private _packhouseConnectionsClientChangeRequestsGQL = inject(
    PackhouseConnectionsClientChangeRequestsGQL,
  );
  private addInternalPackhouseGQL = inject(AddInternalPackhouseGQL);
  private internalPackhouseClientChangeRequestsGQL = inject(
    InternalPackhouseClientChangeRequestsGQL,
  );
  private createSoftwareUpdateReportGQL = inject(CreateSoftwareUpdateReportGQL);
  private packhouseSoftwareUpdateReportsGQL = inject(
    PackhouseSoftwareUpdateReportsGQL,
  );

  // packhouse typenames
  readonly ON_FARM_PACKHOUSE_TYPENAME: string = 'Pack House (On-Farm)';
  readonly OFF_FARM_PACKHOUSE_TYPENAME: string = 'Pack House (Off-Farm)';

  constructor() {}

  extractPackhouseConfigs(
    masterfileConfigs: MasterfileConfig[],
  ): MasterfileConfig[] {
    if (!masterfileConfigs) {
      return [];
    }

    return masterfileConfigs.filter((config: MasterfileConfig) => {
      const packhouse: FoodBusinessOperator =
        config.masterfileEntity as FoodBusinessOperator;

      if (!packhouse || !packhouse.typeOfBusiness) {
        return false;
      }

      return (
        packhouse.typeOfBusiness === this.ON_FARM_PACKHOUSE_TYPENAME ||
        packhouse.typeOfBusiness === this.OFF_FARM_PACKHOUSE_TYPENAME
      );
    });
  }

  transformToConfirmFBOs(packhouseData: MasterfileConfig[]): IConfirmFBO[] {
    if (!packhouseData) {
      return [];
    }

    return packhouseData
      .map((config: MasterfileConfig) => {
        if (!config || !config.masterfileEntity) {
          return null;
        }

        const fbo: FoodBusinessOperator =
          config.masterfileEntity as FoodBusinessOperator;

        const confirmFbo: IConfirmFBO = {
          masterfileConfig: config,
          fbo: structuredClone(fbo),
          isActive: this.isConfigConfirmed(config),
        };

        return confirmFbo;
      })
      .filter((item): item is IConfirmFBO => item !== null);
  }

  isConfigConfirmed(config: MasterfileConfig): boolean {
    return (
      config.state === MasterfileConfigStateEnum.Confirmed ||
      config.state === MasterfileConfigStateEnum.NeedsConfirmation
    );
  }

  fetchPackhouseConnections$(): Observable<PackhouseConnection[]> {
    return this.apollo
      .query<PackhouseConnectionsQuery>({
        query: this.packhouseConnectionsGQL.document,
        fetchPolicy: 'no-cache',
      })
      .pipe(
        map((res) => res.data.packhouseConnections as PackhouseConnection[]),
        map((packhouseConnections: PackhouseConnection[]) => {
          const collator = new Intl.Collator(undefined, {
            numeric: true,
            sensitivity: 'base',
          });
          return packhouseConnections.sort((a, b) =>
            collator.compare(a.name, b.name),
          );
        }),
      );
  }

  fetchSoftwareUpdateReports$(): Observable<SoftwareUpdateReportRequest[]> {
    return this.apollo
      .query<PackhouseSoftwareUpdateReportsQuery>({
        query: this.packhouseSoftwareUpdateReportsGQL.document,
        fetchPolicy: 'no-cache',
      })
      .pipe(map((res) => res.data.packhouseSoftwareUpdateReports));
  }

  fetchPackhouseConnectionsClientChangeRequests$(): Observable<
    PackhouseConnectionClientChangeRequest[]
  > {
    return this.apollo
      .query<PackhouseConnectionsClientChangeRequestsQuery>({
        query: this._packhouseConnectionsClientChangeRequestsGQL.document,
        fetchPolicy: 'no-cache',
      })
      .pipe(
        map((res) => {
          return res.data
            .packhouseConnectionsClientChangeRequests as PackhouseConnectionClientChangeRequest[];
        }),
      );
  }

  fetchMasterfileConfigs$(
    variables: MasterfileConfigsQueryVariables,
  ): Observable<MasterfileConfig[]> {
    const masterfileConfigs$ = this.apollo.query<MasterfileConfigsQuery>({
      query: this.masterfileConfigsGQL.document,
      variables: variables,
      fetchPolicy: 'no-cache',
    });

    return masterfileConfigs$.pipe(
      map((res) => res.data.masterfileConfigs as MasterfileConfig[]),
    );
  }

  confirmMasterfileConfigFbo$(
    masterfileConfigs: MasterfileConfigFboInput[],
  ): Observable<FormattedExecutionResult<ConfirmMasterfileConfigFboMutation>> {
    return this.apollo.mutate<ConfirmMasterfileConfigFboMutation>({
      mutation: this.confirmMasterfileConfigFboGQL.document,
      variables: {
        masterfileConfigs: masterfileConfigs,
      },
    });
  }

  confirmMasterfileConfigPackhouseConnections$(
    masterfileConfigs: MasterfileConfigPackhouseConnectionAttributes[],
  ): Observable<
    FormattedExecutionResult<ConfirmMasterfileConfigPackhouseConnectionsMutation>
  > {
    return this.apollo.mutate<ConfirmMasterfileConfigPackhouseConnectionsMutation>(
      {
        mutation: this.confirmMasterfileConfigPackhouseConnectionsGQL.document,
        variables: {
          masterfileConfigs: masterfileConfigs,
        },
      },
    );
  }

  createPackhouseConnectionsRequest$(
    attributes: CreatePackhouseConnectionsAttribute,
  ): Observable<PackhouseConnectionClientChangeRequest[]> {
    return this.apollo
      .mutate<CreatePackhouseConnectionsRequestMutation>({
        mutation: this.createPackhouseConnectionsRequestGQL.document,
        variables: { attributes: attributes },
      })
      .pipe(
        map((res) => {
          if (res.data.createPackhouseConnectionsRequest?.errors?.length) {
            throw new Error(
              res.data.createPackhouseConnectionsRequest.errors.join(', '),
            );
          }

          return (
            res.data.createPackhouseConnectionsRequest?.clientChangeRequests ??
            []
          );
        }),
      );
  }

  /**
   * Fetches packhouse data by fetching masterfile configs and extracting packhouses.
   * This is a simplified version of the data confirmation service's fetchConfirmationData$.
   */
  fetchPackhouseData$(
    attributes: MasterfileConfigsQueryVariables,
  ): Observable<IConfirmFBO[]> {
    return this.fetchMasterfileConfigs$(attributes).pipe(
      map((masterfileConfigs: MasterfileConfig[]) => {
        const packhouseConfigs =
          this.extractPackhouseConfigs(masterfileConfigs);
        return this.transformToConfirmFBOs(packhouseConfigs);
      }),
    );
  }

  addInternalPackhouse$(
    attributes: AddInternalPackhouseAttributes,
  ): Observable<FormattedExecutionResult<AddInternalPackhouseMutation>> {
    return this.apollo.mutate<AddInternalPackhouseMutation>({
      mutation: this.addInternalPackhouseGQL.document,
      variables: {
        attributes: attributes,
      },
    });
  }

  createSoftwareUpdateReport$(
    selectedLabels: string[],
  ): Observable<FormattedExecutionResult<CreateSoftwareUpdateReportMutation>> {
    return this.apollo.mutate<CreateSoftwareUpdateReportMutation>({
      mutation: this.createSoftwareUpdateReportGQL.document,
      variables: {
        softwareUpdateReport: selectedLabels,
      },
    });
  }

  fetchInternalPackhouseChangeRequests$(): Observable<
    InternalPackhouseClientChangeRequest[]
  > {
    return this.apollo
      .query<InternalPackhouseClientChangeRequestsQuery>({
        query: this.internalPackhouseClientChangeRequestsGQL.document,
        fetchPolicy: 'no-cache',
      })
      .pipe(
        map(
          (res) =>
            res.data
              .internalPackhouseClientChangeRequests as InternalPackhouseClientChangeRequest[],
        ),
      );
  }

  fetchOnFarmPackhouses$(): Observable<FoodBusinessOperator[]> {
    const onFarmPackhouseEnumValue = 1;
    return this.apollo
      .query<FoodBusinessOperatorsByTypeQuery>({
        query: this._foodBusinessOperatorsByTypeGQL.document,
        variables: {
          typeOfBusiness: onFarmPackhouseEnumValue,
        },
      })
      .pipe(
        map((res) => {
          return res.data.foodBusinessOperatorsByType;
        }),
      );
  }

  fetchOffFarmPackhouses$(): Observable<FoodBusinessOperator[]> {
    const offFarmPackhouseEnumValue = 5;
    return this.apollo
      .query<FoodBusinessOperatorsByTypeQuery>({
        query: this._foodBusinessOperatorsByTypeGQL.document,
        variables: {
          typeOfBusiness: offFarmPackhouseEnumValue,
        },
      })
      .pipe(
        map((res) => {
          return res.data.foodBusinessOperatorsByType;
        }),
      );
  }

  /**
   * Fetches all known, unassociated packhouses by fetching on-farm and off-farm packhouses.
   */
  fetchExternalPackhouses$(): Observable<FoodBusinessOperator[]> {
    const collator = new Intl.Collator(undefined, {
      numeric: true,
      sensitivity: 'base',
    });

    return zip(
      this.fetchOnFarmPackhouses$(),
      this.fetchOffFarmPackhouses$(),
    ).pipe(
      map(
        ([onFarmPackhouses, offFarmPackhouses]: [
          FoodBusinessOperator[],
          FoodBusinessOperator[],
        ]) => {
          return [...onFarmPackhouses, ...offFarmPackhouses].sort((a, b) =>
            collator.compare(a.fboCode, b.fboCode),
          );
        },
      ),
    );
  }
}
